import React, { useState, useRef, useEffect, useCallback } from "react";
import { Trans } from "react-i18next";
import TextareaAutosize from "react-textarea-autosize";
import moment from "moment";
import markdownit from "markdown-it";
import { SyncLoader } from "react-spinners";
// import { ReactMic } from "react-mic";
import ReactAudioPlayer from "react-audio-player";
import { ebookChatbotWidgetProps } from "./interface";
import { BotIcon, Send, Trash2, CircleXIcon } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import authService from "../../../utils/authService";

const md = markdownit({
  html: true,
  linkify: true,
  typographer: true,
});

const baseURL = process.env.REACT_APP_PUBLIC_BACKEND_URL || "http://localhost:8000";

// Get WebSocket URL from baseURL
const getWebSocketURL = (): string => {
  const url = new URL(baseURL);
  const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${wsProtocol}//${url.host}/api/assistant/ws`;
};

type IMessage = {
  type: string;
  variant: string;
  text: string;
  attachment: string;
  timestamp: string;
};

export default function EbookChatbotWidget({
  customStyle,
  defaultInput = "",
  currentTitle = "",
  currentBook,
  onClose,
}: ebookChatbotWidgetProps) {
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const sessionIdRef = useRef<string>(uuidv4());
  const currentResponseRef = useRef<string>("");
  const currentRunIdRef = useRef<string | null>(null);
  
  const [isLoading, setIsLoading] = useState(false);
  const [isChat, setIsChat] = useState(false);
  const assistantId = currentBook?.assistant_id;

  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<IMessage[]>([
    {
      type: "bot",
      variant: "text",
      text: "Hello! I'm your AI assistant for this book. How can I help you?",
      attachment: "",
      timestamp: moment().format("hh:mm"),
    },
  ]);
  const [input, setInput] = useState(defaultInput);

  // Function to clear chat history
  const clearChatHistory = () => {
    const welcomeMessage: IMessage = {
      type: "bot",
      variant: "text",
      text: "Hello! I'm your AI assistant for this book. How can I help you?",
      attachment: "",
      timestamp: moment().format("hh:mm"),
    };
    setMessages([welcomeMessage]);
    // Generate new session_id for new conversation
    sessionIdRef.current = uuidv4();
    setThreadId(null);
  };

  // Add styles to enable text selection
  const messageStyles: React.CSSProperties = {
    ...(customStyle?.messages || {}),
    userSelect: "text" as const,
    WebkitUserSelect: "text" as const,
    cursor: "text",
  };

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((data: any) => {
    switch (data.type) {
      case 'thread_created':
        setThreadId(data.thread_id);
        console.log('Thread created:', data.thread_id);
        break;

      case 'message_received':
        // Show "Assistant is thinking..." indicator
        setIsLoading(true);
        setIsChat(true);
        break;

      case 'run_created':
        currentRunIdRef.current = data.run_id;
        console.log('Run started:', data.run_id);
        break;

      case 'response_start':
        currentResponseRef.current = '';
        // Keep loading visible until first chunk arrives
        // Add placeholder message for streaming
        setMessages((prev) => [
          ...prev,
          {
            type: 'bot',
            variant: 'text',
            text: '',
            attachment: '',
            timestamp: moment().format('hh:mm'),
          },
        ]);
        break;

      case 'chunk':
        // Hide loading when first chunk arrives
        setIsLoading(false);
        const chunkContent = data.content || '';
        currentResponseRef.current += chunkContent.replace(/\\n/g, "\n").replace(/\\t/g, "\t");
        // Update the last bot message with accumulated content
        setMessages((prev) => {
          const newMessages = [...prev];
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage && lastMessage.type === 'bot') {
            lastMessage.text = currentResponseRef.current;
          }
          return newMessages;
        });
        break;

      case 'run_completed':
        console.log('Run completed:', data.run_id);
        currentRunIdRef.current = null;
        break;

      case 'response_end':
        console.log('Full response:', data.full_response);
        console.log('Thread ID:', data.thread_id);
        if (data.thread_id) {
          setThreadId(data.thread_id);
        }
        setIsLoading(false);
        setIsChat(false);
        break;

      case 'error':
        console.error('WebSocket error:', data.message);
        setIsLoading(false);
        setIsChat(false);
        setMessages((prev) => [
          ...prev,
          {
            type: 'bot',
            variant: 'text',
            text: `Error: ${data.message || 'An error occurred'}`,
            attachment: '',
            timestamp: moment().format('hh:mm'),
          },
        ]);
        break;

      case 'history':
        // Load chat history
        if (data.messages && Array.isArray(data.messages)) {
          const historyMessages: IMessage[] = data.messages.map((msg: any) => ({
            type: msg.type === 'user' ? 'user' : 'bot',
            variant: 'text',
            text: msg.content || '',
            attachment: '',
            timestamp: moment().format('hh:mm'),
          }));
          setMessages(historyMessages);
        }
        break;

      default:
        console.log('Unknown message type:', data.type);
    }
  }, []);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    // Reuse existing connection if already open
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return; // Already connected
    }

    // Close existing connection if in a bad state
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CONNECTING) {
      wsRef.current.close();
      wsRef.current = null;
    }

    const userData = authService.getUserData();
    const userId = userData?.id || 'anonymous';
    
    // Build WebSocket URL with query parameters
    const wsUrl = `${getWebSocketURL()}?user_id=${userId}${assistantId ? `&assistant_id=${assistantId}` : ''}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected for ebook assistant');
        
        // Optionally create thread explicitly
        if (assistantId) {
          ws.send(JSON.stringify({
            type: 'create_thread'
          }));
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setIsLoading(false);
      };

      ws.onclose = () => {
        console.log('WebSocket closed');
        // Optionally attempt to reconnect
      };
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
    }
  }, [assistantId, handleWebSocketMessage]);

  // Connect WebSocket when assistantId is available
  useEffect(() => {
    if (assistantId) {
      connectWebSocket();
    }

    // Cleanup WebSocket on unmount
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [assistantId, connectWebSocket]);

  const sendQuestion = async () => {
    if (input.trim() === "" || isLoading) return;

    if (!assistantId) {
      console.error("No assistant ID available");
      return;
    }

    setIsChat(true);
    setIsLoading(true);

    const newMessage: IMessage = {
      type: "user",
      variant: "text",
      text: input,
      attachment: "",
      timestamp: moment().format("hh:mm"),
    };

    setMessages((prev) => [...prev, newMessage]);
    const messageText = input;
    setInput("");

    // Connect WebSocket if not connected
    connectWebSocket();

    // Wait for WebSocket to be ready
    const sendMessage = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'message',
          session_id: sessionIdRef.current,
          assistant_id: assistantId,
          thread_id: threadId, // Optional, will be created if not provided
          message: messageText
        }));
      } else if (wsRef.current?.readyState === WebSocket.CONNECTING) {
        // Wait a bit and try again
        setTimeout(sendMessage, 100);
      } else {
        // Connection failed, try to reconnect
        connectWebSocket();
        setTimeout(sendMessage, 500);
      }
    };

    sendMessage();
  };

  // Get chat history (available for future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getChatHistory = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'get_history',
        session_id: sessionIdRef.current
      }));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && e.shiftKey) {
      e.preventDefault();
      setInput(input + "\n");
    } else if (e.key === "Enter") {
      e.preventDefault();
      sendQuestion();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    sendQuestion();
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTo(0, 10000000000);
    }
  }, [messages]);

  // Audio handling functions (for future use when recording buttons are enabled)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // const startRecording = () => {
  //   setRecord(true);
  // };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // const stopRecording = () => {
  //   setRecord(false);
  //   setIsWaitingAIVoice(true);
  // };

  // const onData = (recordedBlob: any) => {
  //   console.log("chunk of real-time data is: ", recordedBlob);
  // };

  return (
    <div className="fixed bottom-20 right-6 w-[calc(100vw-3rem)] sm:w-[28rem] lg:w-[32rem] h-[calc(100vh-8rem)] sm:h-[36rem] lg:h-[40rem] max-h-[90vh] bg-app dark:bg-app-2 backdrop-blur-xl border border-app-border rounded-2xl shadow-2xl flex flex-col z-chatbot animate-slide-in-right" style={customStyle?.chat}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-app-border bg-gradient-to-r from-primary-500 to-primary-600 rounded-t-2xl">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <BotIcon className="w-5 h-5 text-white" />
          </div>
          <span className="text-white font-semibold text-sm">
            <Trans>AI Assistant</Trans>
            {currentTitle && (
              <span className="ml-2 text-xs opacity-90">- {currentTitle}</span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={clearChatHistory}
            className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-105"
            title="Clear chat history"
          >
            <Trash2 className="w-4 h-4 text-white" />
          </button>
          <button 
            onClick={onClose || (() => {})}
            className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-105"
            title="Close"
          >
            <CircleXIcon className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>

      {/* Messages Container */}
      <div 
        className="flex-1 overflow-y-auto p-4 space-y-3 bg-app-2 dark:bg-app scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent"
        style={messageStyles} 
        ref={messagesRef}
      >
        {messages.map((msg, index) => (
          <div key={index} className={`flex gap-3 ${msg.type === "user" ? "flex-row-reverse" : "flex-row"}`}>
            {/* Avatar */}
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 shadow-md ${
              msg.type === "user" 
                ? "bg-gradient-to-br from-primary-500 to-primary-600" 
                : "bg-gradient-to-br from-gray-400 to-gray-500"
            }`}>
              {msg.type !== "user" ? (
                <img src="assets/bot_round.png" alt="Bot Avatar" className="w-6 h-6 rounded-full object-cover" />
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-white"
                >
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 5C13.66 5 15 6.34 15 8C15 9.66 13.66 11 12 11C10.34 11 9 9.66 9 8C9 6.34 10.34 5 12 5ZM12 19.2C9.5 19.2 7.29 17.92 6 15.98C6.03 13.99 10 12.9 12 12.9C13.99 12.9 17.97 13.99 18 15.98C16.71 17.92 14.5 19.2 12 19.2Z"
                    fill="currentColor"
                  />
                </svg>
              )}
            </div>

            {/* Message Content */}
            <div className={`max-w-[75%] ${msg.type === "user" ? "flex flex-col items-end" : ""}`}>
              {msg.variant === "text" ? (
                <div
                  className={`px-4 py-2 rounded-2xl text-sm leading-relaxed shadow-sm ${
                    msg.type === "user"
                      ? "bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-br-md"
                      : "bg-app dark:bg-app-2 text-gray-800 dark:text-app-text rounded-bl-md border border-app-border"
                  }`}
                  style={{
                    cursor: "text",
                    userSelect: "text",
                    WebkitUserSelect: "text",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: md.render(msg.text),
                  }}
                />
              ) : (
                <div className="bg-app dark:bg-app-2 rounded-2xl p-2 border border-app-border shadow-sm">
                  <ReactAudioPlayer 
                    style={{ height: "40px", width: "260px" }} 
                    src={msg.attachment} 
                    autoPlay 
                    controls 
                  />
                </div>
              )}
              
              {/* Timestamp */}
              <div className={`text-xs text-gray-500 dark:text-gray-400 mt-1 ${msg.type === "user" ? "text-right" : "text-left"}`}>
                {msg.timestamp}
              </div>
            </div>
          </div>
        ))}

        {/* Loading Indicator */}
        {(isChat && isLoading) && (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-400 to-gray-500 flex items-center justify-center flex-shrink-0 shadow-md">
              <img src="assets/bot_round.png" alt="Bot Avatar" className="w-6 h-6 rounded-full object-cover" />
            </div>
            <div className="bg-app dark:bg-app-2 rounded-2xl rounded-bl-md px-4 py-3 border border-app-border shadow-sm">
              <SyncLoader size={4} color="rgba(1, 121, 202, 1)" loading={isLoading} speedMultiplier={0.7} />
            </div>
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="p-4 border-t border-app-border bg-app/80 dark:bg-app-2/80 backdrop-blur-sm rounded-b-2xl">
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <TextareaAutosize
              className="w-full px-4 py-3 pr-12 bg-app dark:bg-app-2 border border-app-border rounded-2xl text-sm text-gray-800 dark:text-app-text placeholder-app-text-2 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-transparent resize-none shadow-sm transition-all duration-200"
              placeholder="Type your message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              maxRows={4}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            {/* Send Button */}
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={!input.trim()}
              className="w-10 h-10 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 disabled:from-gray-300 disabled:to-gray-400 text-white rounded-full flex items-center justify-center transition-all duration-200 hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed shadow-md"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
